import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_ANALYTICS_KEY = 'cookie_analytics';
const COOKIE_MARKETING_KEY = 'cookie_marketing';

export const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false
  });
  const { t } = useTranslation();

  useEffect(() => {
    const hasConsent = Cookies.get(COOKIE_CONSENT_KEY);
    if (!hasConsent) {
      setIsVisible(true);
    } else {
      setPreferences({
        analytics: Cookies.get(COOKIE_ANALYTICS_KEY) === 'true',
        marketing: Cookies.get(COOKIE_MARKETING_KEY) === 'true'
      });
    }
  }, []);

  const savePreferences = (analytics: boolean, marketing: boolean) => {
    Cookies.set(COOKIE_CONSENT_KEY, 'true', { expires: 365 });
    Cookies.set(COOKIE_ANALYTICS_KEY, analytics.toString(), { expires: 365 });
    Cookies.set(COOKIE_MARKETING_KEY, marketing.toString(), { expires: 365 });

    setPreferences({ analytics, marketing });
    setIsVisible(false);
  };

  const acceptAll = () => {
    savePreferences(true, true);
  };

  const acceptEssential = () => {
    savePreferences(false, false);
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences.analytics, preferences.marketing);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 w-full z-50 bg-bg-primary border-t border-white/10 shadow-lg animate-fadeIn">
      <div className="container-custom py-6">
        {!showPreferences ? (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex-grow">
              <p className="text-text-light mb-2">
                {t('cookieConsent.message')}
              </p>
              <p className="text-text-secondary text-sm">
                {t('cookieConsent.essentialCookiesDescription')}{' '}
                <Link to="/privacy" className="text-gradient-from hover:text-gradient-to">
                  {t('cookieConsent.learnMore')}
                </Link>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 whitespace-nowrap">
              <button
                onClick={() => setShowPreferences(true)}
                className="btn btn-outline"
                aria-label={t('cookieConsent.customize')}
              >
                {t('cookieConsent.customize')}
              </button>
              <button
                onClick={acceptEssential}
                className="btn btn-outline"
                aria-label={t('cookieConsent.essentialOnly')}
              >
                {t('cookieConsent.essentialOnly')}
              </button>
              <button
                onClick={acceptAll}
                className="btn btn-accent"
                aria-label={t('cookieConsent.acceptAll')}
              >
                {t('cookieConsent.acceptAll')}
              </button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <button
              onClick={() => setShowPreferences(false)}
              className="absolute right-0 top-0 text-text-secondary hover:text-text-light"
              aria-label="Close preferences"
            >
              <X size={24} />
            </button>
            
            <h2 className="text-xl font-bold mb-4">{t('cookieConsent.preferencesHeading')}</h2>
            
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-4">
                <div className="pt-0.5">
                  <input
                    type="checkbox"
                    id="essential"
                    checked
                    disabled
                    className="rounded border-white/20 bg-white/10 text-gradient-from"
                  />
                </div>
                <div>
                  <label htmlFor="essential" className="font-medium block mb-1">
                    {t('cookieConsent.essentialCookies')}
                  </label>
                  <p className="text-text-secondary text-sm">
                    {t('cookieConsent.essentialCookiesDescription')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="pt-0.5">
                  <input
                    type="checkbox"
                    id="analytics"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      analytics: e.target.checked
                    }))}
                    className="rounded border-white/20 bg-white/10 text-gradient-from"
                  />
                </div>
                <div>
                  <label htmlFor="analytics" className="font-medium block mb-1">
                    {t('cookieConsent.analyticsCookies')}
                  </label>
                  <p className="text-text-secondary text-sm">
                    {t('cookieConsent.analyticsCookiesDescription')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="pt-0.5">
                  <input
                    type="checkbox"
                    id="marketing"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      marketing: e.target.checked
                    }))}
                    className="rounded border-white/20 bg-white/10 text-gradient-from"
                  />
                </div>
                <div>
                  <label htmlFor="marketing" className="font-medium block mb-1">
                    {t('cookieConsent.marketingCookies')}
                  </label>
                  <p className="text-text-secondary text-sm">
                    {t('cookieConsent.marketingCookiesDescription')}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={acceptEssential}
                className="btn btn-outline"
                aria-label={t('cookieConsent.essentialOnly')}
              >
                {t('cookieConsent.essentialOnly')}
              </button>
              <button
                onClick={saveCustomPreferences}
                className="btn btn-accent"
                aria-label={t('cookieConsent.savePreferences')}
              >
                {t('cookieConsent.savePreferences')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;